<script setup lang="ts">
import { ref } from "vue";

import { caseService } from "@/services";
import { isApiError } from "@/services/Api";

import { useBrandStore } from "@/stores/brand-store";

import {
  CASE_REFERENCE_LENGTH_MAX,
  CASE_REFERENCE_LENGTH_MIN,
  getCaseReferenceValidationMessage,
  validateCaseReference,
} from "@/utils/case-reference-validation";

withDefaults(defineProps<{ source: string }>(), {  })
const committedCaseReference = defineModel<string>({ required: true });

const caseReference = ref("");

const state = ref<
  | { status: "IDLE"; reference?: null }
  | { status: "LOADING" | "EXISTS" | "NOT_EXISTS" | "FORBIDDEN"; reference: string }
>({ status: "IDLE" });

const errorMessage = ref<string | undefined>();

async function checkDestinationCase() {
  state.value = { status: "IDLE" };

  errorMessage.value = (() => {
    const validationError = validateCaseReference(caseReference.value);
    if (validationError != null) return getCaseReferenceValidationMessage(validationError);

    if (caseReference.value.toLowerCase() === __props.source.toLowerCase())
      return "The destination case must be different to the source case.";
  })();

  if (errorMessage.value != null) return;

  const { selectedClient, selectedBrand } = useBrandStore();
  if (selectedClient == null || selectedBrand == null) return;

  const reference = caseReference.value;
  committedCaseReference.value = reference;

  try {
    state.value = { status: "LOADING", reference };

    await caseService.GetCaseMetadata(selectedClient, selectedBrand, reference);
    if (caseReference.value !== reference) return;

    state.value = { status: "EXISTS", reference };
  } catch (error) {
    if (caseReference.value !== reference) return;
    state.value = { status: isApiError(error) && error.status === 403 ? "FORBIDDEN" : "NOT_EXISTS", reference };
  }
}
</script>
<template>
  <div class="tw-flex tw-flex-col tw-gap-1">
    <label for="migration-destination-case" class="tw-text-xs">Destination Case Reference</label>
    <div class="tw-flex tw-items-center tw-gap-2">
      <input
        id="migration-destination-case"
        v-model.trim="caseReference"
        type="text"
        required
        :aria-invalid="errorMessage != null"
        aria-describedby="migration-destination-case-desc"
        class="tw-w-full tw-rounded tw-border tw-border-gray-500 tw-p-2 tw-text-sm"
        placeholder="Type a case reference..."
        :maxlength="CASE_REFERENCE_LENGTH_MAX"
        :minlength="CASE_REFERENCE_LENGTH_MIN"
        @update:model-value="committedCaseReference = ''"
      />
      <button
        type="button"
        class="tw-btn-md-gray tw-shrink-0"
        :disabled="caseReference === '' || state.status === 'LOADING'"
        @click="checkDestinationCase"
      >
        <i v-if="state.status === 'LOADING'" class="fas fa-spinner fa-sm fa-spin" aria-hidden="true" />
        Validate Case
      </button>
    </div>
    <p v-if="errorMessage != null" id="migration-destination-case-desc" role="alert" class="tw-text-red-600">
      {{ errorMessage }}
    </p>
    <p
      v-else-if="state.status !== 'IDLE' && state.reference === caseReference"
      id="migration-destination-case-desc"
      class="tw-flex tw-items-center tw-gap-2"
      :class="{
        'tw-text-green-700': state.status === 'EXISTS',
        'tw-text-red-600': state.status === 'FORBIDDEN',
        'tw-text-orange-700': state.status === 'NOT_EXISTS',
      }"
    >
      <template v-if="state.status === 'EXISTS'">
        <i class="fas fa-circle-check" aria-hidden="true" />
        Case "{{ state.reference }}" exists and can be used as the destination.
      </template>
      <template v-else-if="state.status === 'FORBIDDEN'">
        <i class="fas fa-triangle-exclamation" aria-hidden="true" />
        Case "{{ state.reference }}" exists, but you do not have access to it.
      </template>
      <template v-else-if="state.status === 'NOT_EXISTS'">
        <i class="fas fa-circle-info" aria-hidden="true" />
        Case "{{ state.reference }}" does not exist yet. It will be created automatically as part of the migration.
      </template>
      <template v-else-if="state.status === 'LOADING'">
        <i class="fas fa-spinner fa-sm fa-spin" aria-hidden="true" />
        Checking case "{{ state.reference }}"...
      </template>
    </p>
    <p v-else>Validate the Destination Case before continuing.</p>
  </div>
</template>
